<template>
  <Teleport to="body">
    <transition name="cell-type-popup__fade">
      <div
        v-if="visible"
        ref="popupRef"
        class="cell-type-popup"
        :style="popupStyle"
        role="menu"
        tabindex="-1"
      >
        <header
          v-if="currentLabel"
          class="cell-type-popup__header"
        >
          Current label: <strong>{{ currentLabel }}</strong>
        </header>
        <CellTypeMenuContent
          :options="menuOptions"
          @select="handleSelect"
        />
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { PropType } from "vue";
import { getDrawOptions, type CellTypeMenuOption } from "@/utils/constants/cellTypeMenuConfig";
import { useConstants } from "@/composables/useAppGlobals";
import { usePopupBehavior, type PopupPosition } from "@/composables/usePopupBehavior";
import CellTypeMenuContent from "./CellTypeMenuContent.vue";

const props = defineProps({
  visible: { type: Boolean, default: false },
  position: {
    type: Object as PropType<PopupPosition>,
    default: () => ({ x: 0, y: 0 }),
  },
  currentLabel: { type: String, default: "" },
  options: {
    type: Array as PropType<CellTypeMenuOption[]>,
    default: null,
  },
});

const emit = defineEmits<{
  (e: "select", option: CellTypeMenuOption): void;
  (e: "close"): void;
}>();

const $const = useConstants();

const menuOptions = computed<CellTypeMenuOption[]>(() =>
  props.options?.length ? props.options : getDrawOptions({ isRbc: true, consts: $const }),
);

const popupRef = ref<HTMLDivElement | null>(null);
const { popupStyle } = usePopupBehavior({
  visible: () => props.visible,
  position: () => props.position,
  popupRef,
  onClose: () => emit("close"),
});

function handleSelect(option: CellTypeMenuOption) {
  emit("select", option);
}

</script>

<style scoped>
.cell-type-popup {
  position: fixed;
  z-index: 3000;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.15);
  min-width: 520px;
  padding: 12px 16px 16px;
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.25);
  outline: none;
}

.cell-type-popup__header {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  margin-bottom: 0.4rem;
}

.cell-type-popup__fade-enter-active,
.cell-type-popup__fade-leave-active {
  transition: opacity 120ms ease;
}

.cell-type-popup__fade-enter-from,
.cell-type-popup__fade-leave-to {
  opacity: 0;
}
</style>
